import { useTranslation } from "@/lib/i18n";
import { useState } from "react";
import { ChevronLeft, ChevronRight, Calendar, Loader2 } from "lucide-react";

function getDays(centerDate, count = 7) {
  const days = [];
  const start = new Date(centerDate);
  start.setDate(start.getDate() - Math.floor(count / 2));
  for (let i = 0; i < count; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push(d);
  }
  return days;
}

function isSameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function formatTime(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" });
}

function getDayName(date, lang) {
  return date.toLocaleDateString(lang || 'pt', { weekday: 'short' });
}
function getMonthName(date, lang) {
  return date.toLocaleDateString(lang || 'pt', { month: 'short' });
}

export default function DayCalendar({ selectedDate, onDateChange, dailyFixtures = [], loadingDaily = false, onFixtureSelect, loadingFixtureId }) {
  const [centerDate, setCenterDate] = useState(new Date());
  const days = getDays(centerDate, 7);
  const { lang } = useTranslation();

  const shiftDays = (direction) => {
    const next = new Date(centerDate);
    next.setDate(next.getDate() + direction * 7);
    setCenterDate(next);
  };

  // Group by league, sort by date within each league
  const grouped = {};
  dailyFixtures.forEach((f) => {
    const key = f.league_name || f.league_code || "Outros";
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(f);
  });
  Object.keys(grouped).forEach(key => {
    grouped[key].sort((a, b) => new Date(a.date) - new Date(b.date));
  });

  return (
    <div className="w-full space-y-4">
      {/* Calendar Row */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => shiftDays(-1)}
          className="p-1.5 bg-surface border border-line rounded-sm text-txt-secondary hover:text-white hover:bg-surface-hover transition-colors shrink-0"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        <div className="flex gap-1.5 flex-1 overflow-x-auto pb-1">
          {days.map((day, i) => {
            const isToday = isSameDay(day, new Date());
            const isSelected = isSameDay(day, selectedDate);
            return (
              <button
                key={i}
                onClick={() => onDateChange(day)}
                className={`
                  flex flex-col items-center px-3 py-2 rounded-sm border transition-all shrink-0 min-w-[52px]
                  ${isSelected
                    ? "bg-volt/10 border-volt text-volt"
                    : isToday
                    ? "bg-surface border-volt/40 text-white"
                    : "bg-surface border-line text-txt-secondary hover:bg-surface-hover hover:text-white"
                  }
                `}
              >
                <span className="text-[10px] font-bold uppercase tracking-wider">
                  {getDayName(day, lang)}
                </span>
                <span className="text-lg font-black leading-none mt-0.5">
                  {day.getDate()}
                </span>
                <span className="text-[9px] opacity-60 mt-0.5">
                  {getMonthName(day, lang)}
                </span>
                {isToday && (
                  <span className="w-1 h-1 rounded-full bg-volt mt-1" />
                )}
              </button>
            );
          })}
        </div>

        <button
          onClick={() => shiftDays(1)}
          className="p-1.5 bg-surface border border-line rounded-sm text-txt-secondary hover:text-white hover:bg-surface-hover transition-colors shrink-0"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Fixtures */}
      {loadingDaily ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 text-volt animate-spin" />
        </div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="bg-surface border border-line rounded-sm p-6 text-center">
          <Calendar className="w-8 h-8 text-txt-secondary mx-auto mb-2" />
          <p className="text-txt-secondary text-sm">
            Sem jogos para {selectedDate.toLocaleDateString("pt-PT", { weekday: "long", day: "2-digit", month: "long" })}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {Object.entries(grouped).map(([leagueName, matches]) => (
            <div key={leagueName} className="bg-surface border border-line rounded-sm overflow-hidden">
              {/* League Header */}
              <div className="px-4 py-2 bg-surface-hover border-b border-line">
                <span className="font-heading text-xs font-bold uppercase tracking-[0.15em] text-volt">
                  {leagueName}
                </span>
              </div>

              {/* Matches grouped by day */}
              <div className="divide-y divide-line/40">
                {matches.map((match, idx) => {
                  const matchDate = new Date(match.date);
                  const prevMatch = idx > 0 ? matches[idx - 1] : null;
                  const prevDate = prevMatch ? new Date(prevMatch.date) : null;
                  const showDateHeader = !prevDate ||
                    matchDate.toDateString() !== prevDate.toDateString();
                  const isLoading = loadingFixtureId === match.fixture_id;

                  return (
                    <div key={match.fixture_id}>
                      {showDateHeader && (
                        <div className="px-4 py-1.5 bg-obsidian/50 border-b border-line/30">
                          <span className="text-[10px] font-bold uppercase tracking-widest text-txt-secondary">
                            {matchDate.toLocaleDateString(lang || "pt-PT", {
                              weekday: "long", day: "2-digit", month: "long",
                              timeZone: "Europe/Lisbon"
                            })}
                          </span>
                        </div>
                      )}
                      <button
                        onClick={() => onFixtureSelect && onFixtureSelect(match.fixture_id, match.league_code)}
   
                        disabled={isLoading}
                        className="w-full flex items-center justify-between px-4 py-3 hover:bg-surface-hover transition-colors group text-left"
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <span className="font-mono text-sm text-txt-secondary w-12 shrink-0">
                            {formatTime(match.date)}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              {match.home?.logo && (
                                <img src={match.home.logo} alt="" className="w-5 h-5 object-contain shrink-0" />
                              )}
                              <span className="text-sm font-semibold text-white truncate">
                                {match.home?.name}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 mt-0.5">
                              {match.away?.logo && (
                                <img src={match.away.logo} alt="" className="w-5 h-5 object-contain shrink-0" />
                              )}
                              <span className="text-sm text-txt-secondary truncate">
                                {match.away?.name}
                              </span>
                            </div>
                          </div>
                        </div>
                        {match.status === "FT" && match.goals ? (
                          <div className="font-mono text-sm font-bold text-white mr-3">
                            {match.goals.home} - {match.goals.away}
                          </div>
                        ) : (
                          <span className="text-[10px] font-bold uppercase tracking-widest text-volt bg-volt/10 px-2 py-0.5 rounded-sm mr-3">
                            {match.status === "NS" ? "PRE" : match.status || "PRE"}
                          </span>
                        )}
                        {isLoading ? (
                          <Loader2 className="w-4 h-4 text-volt animate-spin" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-txt-secondary group-hover:text-volt transition-colors" />
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}